'use client';

import { PERMISSIONS } from '@terra/shared-authorization';
import { ADAPTIVE_SCANNING_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import {
  AdaptiveScanningCard as AdaptiveScanningCardView,
  AdaptiveScanningResumeDialog,
} from '@terra/ui-design-system';
import { useAdaptiveScanningState } from '../../hooks/api/useAdaptiveScanningState';
import { useSetAdaptiveScanningEnabled } from '../../hooks/api/useSetAdaptiveScanningEnabled';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { usePermissions } from '../../hooks/usePermissions';
import { useAdaptiveScanningResume } from './automation/useAdaptiveScanningResume';
import { useNavigateToBlockedSignals } from './automation/useNavigateToBlockedSignals';

type AdaptiveScanningCardProps = {
  applicationId: string;
  isReadOnly?: boolean;
};

export function AdaptiveScanningCard({
  applicationId,
  isReadOnly,
}: AdaptiveScanningCardProps) {
  const { isFeatureFlagEnabled } = useFeatureFlags(
    ADAPTIVE_SCANNING_ENABLED_FLAG,
  );
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.MANAGE_APPLICATIONS.name);
  const { data } = useAdaptiveScanningState({
    applicationId,
    enabled: isFeatureFlagEnabled,
  });
  const setEnabledMutation = useSetAdaptiveScanningEnabled({ applicationId });
  const { isResumeOpen, setIsResumeOpen, isResuming, confirmResume } =
    useAdaptiveScanningResume({ applicationId });
  const navigateToBlockedSignals = useNavigateToBlockedSignals({
    applicationId,
  });

  if (!isFeatureFlagEnabled || !data) {
    return null;
  }

  return (
    <>
      <AdaptiveScanningCardView
        state={data}
        canToggle={canManage && !isReadOnly}
        isToggling={setEnabledMutation.isPending}
        isResuming={isResuming}
        onToggle={(enabled) => setEnabledMutation.mutate(enabled)}
        onResumeClick={() => setIsResumeOpen(true)}
        onViewBlocked={navigateToBlockedSignals}
      />
      <AdaptiveScanningResumeDialog
        open={isResumeOpen}
        state={data}
        isSubmitting={isResuming}
        onOpenChange={setIsResumeOpen}
        onConfirm={confirmResume}
      />
    </>
  );
}
